import { ReactElement, useEffect, useState } from "react";
import { IAxisLinear, ILineChartConfig, IPoint, IStackedBarChartConfig } from "./types";
import Chart from "./chart";
import { getTicks, useChartDimensions, useUniqueId } from "./chartUtils";
import AxisHorizontal from "./AxisHorizontal";
import AxisVertical from "./AxisVertical";
import * as d3 from "d3";
import { useTheme } from "@mui/material";
import Legend from "./legend";
import { getPaletteColor } from "../../configuration/configuration";
import HtmlTooltip from "./tooltip";
import Grid from "./grid";
import { formatNumber, roundUp } from "../../system/utils";

interface IBarChartProps {
  barChartConfig: IStackedBarChartConfig;
  showAccumulated?: boolean;
}

interface IDataSeries {
  name: string;
  value: number;
}

interface IData {
  categories: Record<string, IDataSeries[]>;
  yMax: number;
}

const paddingInner = 0.1;
const paddingOuter = 0.1;
const align = 0.5;

const StackedBarChart = ({ barChartConfig, showAccumulated }: IBarChartProps): ReactElement => {
  const barID = useUniqueId("stacked-bar-chart");

  const theme = useTheme();
  const [ data, setData ] = useState<IData>({ categories: {}, yMax: 0 });
  const [ accumChartConfig, setAccumChartConfig ] = useState<ILineChartConfig | null>(null);
  const [ maxLen, setMaxLen ] = useState(0);

  let headerItems = barChartConfig.title ? 1 : 0;
  headerItems += barChartConfig.showLegend ? 1 : 0;

  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: barChartConfig.height ?? undefined,
    width: barChartConfig.width ?? undefined,
    marginBottom: !barChartConfig.xAxis.hidden ? 35 : 10,
    marginLeft: 10,
    marginRight: 60,
    marginTop: 10 + 20 * headerItems
  });

  useEffect(() => {

    const newData: IData = { categories: {}, yMax: 0 };
    const maxValues: Record<any, number> = {};

    barChartConfig.series.forEach(s => {
      (s.value as IPoint[]).forEach(p => {
        if (!newData.categories[p.x]) {
          newData.categories[p.x] = [];
          maxValues[p.x] = 0;
        }

        newData.categories[p.x].push({ name: s.name, value: p.y });

        maxValues[p.x] += p.y;
      });
    });

    newData.yMax = Math.max(...Object.values(maxValues));

    if (barChartConfig.accumulate) {
      let maximum = 0;
      let nameLen = 0;

      const accumSeries = barChartConfig.series.map(s => {
        let accum = 0;

        const value = (s.value as IPoint[]).map(p => {
          accum += p.y;

          return { x: p.x, y: accum };
        });

        maximum = Math.max(accum, maximum);
        nameLen = Math.max(s.name.length, nameLen);

        return {
          ...s,
          value,
          xAxisName: barChartConfig.xAxis.name,
          yAxisName: "AccumAxis"
        };
      });

      const yAxis: IAxisLinear = {
        name: "AccumAxis",
        minimum: 0,
        maximum: roundUp(maximum, 0),
        ticksCount: 2
      };

      setAccumChartConfig({
        height: barChartConfig.height,
        xAxis: barChartConfig.xAxis,
        yAxis: [ yAxis ],
        series: accumSeries
      });

      setMaxLen(nameLen * 8 + 40);
    }

    setData(newData);
  }, [barChartConfig]);

  if (Object.keys(data).length === 0) {
    return <div/>;
  }


  const xScale = d3.scaleBand()
    .domain(barChartConfig.xAxis.categories ?? barChartConfig.series.map(series => series.name))
    .range([0, dimensions.boundedWidth])
    .paddingInner(paddingInner)
    .paddingOuter(paddingOuter)
    .align(align);

  const yScale = d3.scaleLinear()
    .domain([0, data.yMax])
    .range([dimensions.boundedHeight, 0])
    .nice();

  const accumYScale = accumChartConfig
    ? d3.scaleLinear()
      .domain([(accumChartConfig.yAxis[0] as IAxisLinear).minimum, (accumChartConfig.yAxis[0] as IAxisLinear).maximum] )
      .range([dimensions.boundedHeight, 0])
      .nice()
    : null;

  const lineGenerator = accumYScale
    ? d3.line<IPoint>()
      .x((d: IPoint) => xScale(d.x as any) ?? 0)
      .y((d) => accumYScale(d.y))
      .curve(d3.curveLinear)
    : null;

  const xTicks = xScale.domain();
  const yTicks = getTicks(yScale.domain() as number[], 3);

  const middle = xScale.bandwidth() / 2.0;

  return (
    <div ref={ref} style={{ width: "100%", height: "calc(100% - 10px)" }} >
      <Chart dimensions={dimensions} >
        {/*<defs>*/}
        {/*  <filter id="shadow" x="0" y="0" width="200%" height="200%">*/}
        {/*    <feOffset result="offOut" in="SourceGraphic" dx="1" dy="1" />*/}
        {/*    <feGaussianBlur result="blurOut" in="offOut" stdDeviation="1" />*/}
        {/*    <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />*/}
        {/*  </filter>*/}
        {/*</defs>*/}

        <g>
          { !barChartConfig.xAxis.hidden &&
          <AxisHorizontal
            key={`${barID}-x-axis`}
            chartID={barID}
            dimensions={dimensions}
            scale={xScale}
            label={barChartConfig.xAxis.name}
            fontSize={barChartConfig.xAxis.fontSize}
            ticks={xTicks}
            ticksAngle={barChartConfig.xAxis.ticksAngle}
            formatTick={barChartConfig.xAxis.formatTick}/>
          }

          <AxisVertical
            chartID={barID}
            dimensions={dimensions}
            scale={yScale}
            ticks={yTicks}
            label={barChartConfig.yAxis.name}
            formatTick={s => `${s}`}/>
        </g>

        <text fontSize="1.5em" y={-15 * headerItems} fill={theme.palette.text.primary} className="Axis__label">{barChartConfig.title}</text>

        { barChartConfig.showLegend && <Legend chartID={barID} series={barChartConfig.series} width={dimensions.boundedWidth} /> }

        <Grid charID={barID} xScale={xScale} yScale={yScale} dimensions={dimensions} yTicks={yTicks}/>

        <g>
          { Object.keys(data.categories).map((catName) => {
            const catKey = `${barID}-cat-${catName}`;
            const list = data.categories[catName].sort((a, b) => b.value - a.value);
            let previousValue = 0;

            return (
              <g key={catKey}>
                { list.map((item, index) => {

                  const barX = xScale(catName);
                  if(!barX) {
                    return null;
                  }

                  const preBarY = yScale(previousValue);
                  const barY = yScale(item.value + previousValue);
                  const series = barChartConfig.series.find(s => s.name === item.name);

                  if (!series) {
                    return null;
                  }

                  previousValue += item.value;

                  return(
                    <HtmlTooltip
                      key={`${catKey}-bar-${index}`}
                      title={
                        <div className="display-flex-column">
                          <div className="display-flex-column align-items-center" >
                            <strong>{catName}</strong>
                          </div>
                          <div className="display-flex-row align-items-center" >
                            <div style={{ background: series.color, width: 10, height: 10, marginRight: 10 }} />
                            <strong className="flex-grow-1">{series.name}</strong>
                            <strong className="flex-grow-0">
                              {formatNumber(item.value, 2)}
                              {series.unit && <sub>{`[${series.unit}]`}</sub>}
                            </strong>
                          </div>
                        </div>}>
                      <rect
                        className="Bars__rect"
                        fill={series ? series.color : getPaletteColor()}
                        height={Math.max(0, preBarY - barY)}
                        width={xScale.bandwidth()}
                        y={barY}
                        x={barX}
                      />
                    </HtmlTooltip>
                  );
                })
                }
              </g>
            );
          })
          }
        </g>

        <g>
          {showAccumulated &&
            <g>
              <rect width={dimensions.boundedWidth} height={dimensions.boundedHeight} fill="black" fillOpacity={0.8}/>
              { accumYScale && accumChartConfig && lineGenerator && accumChartConfig.series.map((series, index) => {
                const points = series.value as IPoint[];

                return (
                  <g key={`accum-series-legend-${index}`}>
                    <circle r={5} fill={series.color} cy={20 + index * 15} cx={10} stroke={series.color} strokeWidth={0} />
                    <text y={25 + index * 15} x={25} fill={theme.palette.text.primary} fontSize={"0.9em"}>{series.name}</text>

                    <text y={25 + index * 15} x={maxLen} fill={theme.palette.text.primary} fontSize={"0.9em"} textAnchor={"end"}>
                      { formatNumber(points.at(-1)?.y ?? 0, 2) }
                    </text>
                  </g>
                );
              })
              }
            </g>
          }
          {showAccumulated && accumYScale && accumChartConfig && lineGenerator && accumChartConfig.series.map((series, index) => {
            const key = `accum-series-${index}`;

            return (
              <g key={`accum-series-${index}`} transform={`translate(${middle}, 0)`}>
                <path
                  key={key}
                  className={"Line"}
                  fill="none"
                  stroke={series.color}
                  strokeWidth={2}
                  d={lineGenerator(series.value as any) ?? ""}
                />

                { (series.value as IPoint[]).map((point, pointIndex) => {

                  return (
                    <HtmlTooltip
                      key={`${key}-point-${pointIndex}`}
                      title={
                        <div className="display-flex-column">
                          <div className="display-flex-row align-items-center" >
                            <div style={{ background: series.color, width: 10, height: 10, marginRight: 10 }} />
                            <strong className="flex-grow-1 mr-2">{series.name}</strong>
                            <strong className="flex-grow-0">
                              { formatNumber(point.y, 2) }
                              {series.unit && <sub>{`[${series.unit}]`}</sub>}
                            </strong>
                          </div>
                        </div>
                      }>
                      <circle cx={xScale(point.x as any)} cy={accumYScale(point.y)} r="2" fill="white" stroke={series.color} strokeWidth={1}/>
                    </HtmlTooltip>
                  );
                })}
              </g>
            );
          })}
        </g>

      </Chart>
    </div>);
};

export default StackedBarChart;
